import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { ApiService } from '../../services/api.service';
import { modelLabel as lookupModelLabel } from '../../services/models';

interface ParticipantIdentity { id: number; name: string; isOwner: boolean; }

@Component({
  selector: 'app-meeting-room',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './meeting-room.html',
  styleUrl: './meeting-room.scss'
})
export class MeetingRoomComponent implements OnInit, OnDestroy {
  meetingId = 0;
  meeting: any = null;
  experts: any[] = [];
  messages: any[] = [];
  participants: any[] = [];
  state: any = null;

  loading = true;
  generating = false;       // tur üretiliyor (Claude çağrısı sürüyor)
  finalizing = false;       // final rapor üretiliyor
  sending = false;          // katılımcı mesajı gönderiliyor
  suggestFinalize = false;

  // Katılımcı kimliği (localStorage) + davet akışı
  me: ParticipantIdentity | null = null;
  showJoin = false;
  joinName = localStorage.getItem('aidesk-user-name') || '';
  joining = false;
  inviteCopied = false;

  // Otomatik akış + typewriter
  paused = false;
  private animQueue: any[] = [];
  private animating = false;
  private animTimer: any = null;
  private pollTimer: any = null;
  private knownIds = new Set<number>();
  private destroyed = false;

  userText = '';
  mentionOpen = false;
  mentionQuery = '';
  private mentionStart = -1;
  editingBudget = false;
  newBudget = 0;
  reportHtml: SafeHtml | null = null;
  topicHtml: SafeHtml | null = null;
  showReport = false;

  // Uzman rol etiketlerinin (koyu panelde okunur) renk paleti — avatar sırasıyla eşleşir.
  private readonly rolePalette = ['#d59be0', '#a99bf0', '#e8a56b', '#5fd6a0', '#ef92b0', '#bcd977'];

  @ViewChild('feed') feedRef?: ElementRef<HTMLDivElement>;
  @ViewChild('reportEl') reportRef?: ElementRef<HTMLDivElement>;
  @ViewChild('userTa') taRef?: ElementRef<HTMLTextAreaElement>;

  constructor(private api: ApiService, private route: ActivatedRoute, private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.meetingId = Number(this.route.snapshot.paramMap.get('id'));
    const stored = localStorage.getItem('meeting-participant-' + this.meetingId);
    this.me = stored ? this.tryParse(stored) : null;
    this.showJoin = !this.me;
    this.load(true);
    // Diğer katılımcıların mesajlarını almak için herkes periyodik günceller.
    this.pollTimer = setInterval(() => this.poll(), 3000);
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.animTimer) clearTimeout(this.animTimer);
  }

  load(initial = false) {
    this.api.PostData('meeting/get', { meetingId: this.meetingId }, (resp: any) => {
      this.applyMeeting(resp.meeting);
      this.experts = resp.experts || [];
      this.participants = resp.participants || [];
      this.mergeMessages(resp.messages || [], !initial);
      this.loading = false;
      if (initial) {
        this.scrollToBottom();
        this.maybeContinue();
      }
    }, () => this.loading = false);
  }

  private poll() {
    if (this.loading || this.generating || this.finalizing || this.showJoin) return;
    this.api.PostData('meeting/get', { meetingId: this.meetingId }, (resp: any) => {
      if (this.destroyed) return;
      // Tur üretimi sırasında dönen yanıtla çakışmayı önle.
      if (this.generating || this.finalizing) return;
      this.applyMeeting(resp.meeting);
      this.participants = resp.participants || this.participants;
      this.mergeMessages(resp.messages || [], true);
    });
  }

  private applyMeeting(m: any) {
    this.meeting = m;
    if (m?.status === 'Closing') this.suggestFinalize = true;
    if (!this.editingBudget) this.newBudget = m?.creditBudget || 0;
    this.state = m?.stateJson ? this.tryParse(m.stateJson) : this.state;
    this.topicHtml = m?.topic ? this.sanitizer.bypassSecurityTrustHtml(m.topic) : null;
    if (m?.finalReport) {
      this.reportHtml = this.sanitizer.bypassSecurityTrustHtml(marked.parse(m.finalReport, { async: false }) as string);
    }
  }

  // Düz metin (HTML konu → metin): davet başlığı vb. için.
  plain(html: string): string {
    return (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private tryParse(s: string): any {
    try { return JSON.parse(s); } catch { return null; }
  }

  // ---- Katılım (davet linki) ----

  join() {
    const name = this.joinName.trim();
    if (!name || this.joining) return;
    this.joining = true;
    this.api.PostData('meeting/join', { meetingId: this.meetingId, name }, (resp: any) => {
      // Aynı isimle dönen kayıt sahibin kendisi olabilir; sahipliği sunucudan al.
      this.me = { id: resp.participantId, name: resp.name, isOwner: !!resp.isOwner };
      localStorage.setItem('meeting-participant-' + this.meetingId, JSON.stringify(this.me));
      localStorage.setItem('aidesk-user-name', name);
      this.participants = resp.participants || this.participants;
      this.showJoin = false;
      this.joining = false;
      this.load(true);
    }, () => this.joining = false);
  }

  inviteLink = '';

  copyInvite() {
    const link = location.origin + '/meeting/' + this.meetingId;
    const done = () => {
      this.inviteCopied = true;
      setTimeout(() => this.inviteCopied = false, 2500);
    };
    navigator.clipboard.writeText(link).then(done).catch(() => {
      // Clipboard API izni yoksa (http, iframe vb.) eski yöntemle dene.
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) done();
      else this.inviteLink = link; // kopyalanamadı: linki elle kopyalasın diye göster
    });
  }

  // ---- Bütçe ----

  get budgetPct(): number {
    if (!this.meeting || !this.meeting.creditBudget) return 0;
    return Math.min(100, Math.round((this.meeting.creditsUsed || 0) * 100 / this.meeting.creditBudget));
  }

  get budgetClass(): string {
    const p = this.budgetPct;
    return p >= 90 ? 'bg-danger' : p >= 80 ? 'bg-warning' : 'bg-success';
  }

  modelLabel(id: string): string {
    return lookupModelLabel(id);
  }

  saveBudget() {
    if (this.newBudget < 1) return;
    this.api.PostData('meeting/set-budget', { meetingId: this.meetingId, creditBudget: this.newBudget }, (resp: any) => {
      this.applyMeeting(resp);
      this.editingBudget = false;
    });
  }

  // ---- Otomatik akış ----

  get isOwner(): boolean {
    return !!this.me?.isOwner;
  }

  get flowActive(): boolean {
    return !!this.meeting && this.meeting.status !== 'Completed' && this.meeting.status !== 'Setup'
      && this.meeting.status !== 'Closing' && !this.suggestFinalize && !this.showReport;
  }

  togglePause() {
    this.paused = !this.paused;
    if (!this.paused) this.maybeContinue();
  }

  // Bir sonraki turu tetikle: sadece toplantı sahibi sürer, animasyon bittiğinde zincirlenir.
  private maybeContinue() {
    if (this.destroyed || !this.isOwner || this.paused || this.showJoin) return;
    if (!this.flowActive || this.generating || this.finalizing || this.animating || this.sending) return;
    this.generating = true;
    this.api.PostData('meeting/next', { meetingId: this.meetingId }, (resp: any) => {
      if (resp.busy) {
        // Başka bir sekme/istemci zaten tur üretiyor; poll mesajları getirecek, sonra tekrar dene.
        this.generating = false;
        setTimeout(() => this.maybeContinue(), 4000);
        return;
      }
      this.applyMeeting(resp.meeting);
      this.state = resp.state || this.state;
      this.suggestFinalize = !!resp.suggestFinalize;
      this.generating = false;
      this.mergeMessages(resp.allMessages || [], true);
      // Yeni mesaj gelmediyse (beklenmedik durum) döngüyü durdurma ama kilitlenme de olmasın.
      if (!this.animating) this.scheduleNext();
    }, () => {
      this.generating = false;
      // Hata olduysa otomatik akışı duraklat; kullanıcı Sürdür ile tekrar deneyebilir.
      this.paused = true;
    });
  }

  private scheduleNext() {
    if (this.destroyed) return;
    setTimeout(() => this.maybeContinue(), 900);
  }

  // ---- Mesaj birleştirme + typewriter ----

  private mergeMessages(list: any[], animateNew: boolean) {
    // Kullanıcı geçmişi okumak için yukarı kaydırmışsa alta zıplatma (#3 scroll hatası).
    const wasNearBottom = this.isNearBottom();
    let added = 0;
    for (const m of list) {
      if (this.knownIds.has(m.id)) continue;
      this.knownIds.add(m.id);
      added++;
      const mine = m.role === 'user' && this.me && m.speakerCode === 'P' + this.me.id;
      if (animateNew && !mine && m.role !== 'system' && m.role !== 'user') {
        m._display = '';
        m._animating = false;
        this.messages.push(m);
        this.animQueue.push(m);
      } else {
        m._display = m.content;
        this.messages.push(m);
      }
    }
    if (this.animQueue.length && !this.animating) this.runAnimation();
    if (!animateNew) this.messages.forEach(m => m._display = m.content);
    // Sadece yeni mesaj eklendiyse ve kullanıcı zaten en alttaysa kaydır.
    if (added > 0 && wasNearBottom) this.scrollToBottom();
  }

  // Feed'in en altına yakın mı? (Kullanıcı yukarı kaydırdıysa otomatik kaydırmayı durdur.)
  private isNearBottom(threshold = 140): boolean {
    const el = this.feedRef?.nativeElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }

  private runAnimation() {
    const m = this.animQueue.shift();
    if (!m) {
      this.animating = false;
      this.scheduleNext();
      return;
    }
    this.animating = true;
    m._animating = true;
    // Animasyon başında kullanıcı en alttaysa takip et; yukarı kaydırdıysa rahat bırak.
    const stick = this.isNearBottom();
    const full: string = m.content || '';
    let pos = 0;
    const step = () => {
      if (this.destroyed) return;
      pos = Math.min(full.length, pos + 3); // ~180 karakter/sn
      m._display = full.substring(0, pos);
      if (stick) this.scrollToBottom(0);
      if (pos < full.length) {
        this.animTimer = setTimeout(step, 16);
      } else {
        m._animating = false;
        this.animTimer = setTimeout(() => this.runAnimation(), 350);
      }
    };
    step();
  }

  // ---- Mention (@) ----

  get mentionables(): { label: string; insert: string; icon: string }[] {
    const list: { label: string; insert: string; icon: string }[] = [
      { label: 'Moderatör', insert: 'Moderatör', icon: 'fa-solid fa-gavel' }
    ];
    for (const e of this.experts) {
      if (e.isApproved) list.push({ label: `${e.name} (${e.code} — ${e.title || ''})`, insert: e.name, icon: 'fa-solid fa-robot' });
    }
    for (const p of this.participants) {
      if (!this.me || p.id !== this.me.id) list.push({ label: p.name, insert: p.name, icon: 'fa-solid fa-user' });
    }
    return list;
  }

  get filteredMentions() {
    const q = this.mentionQuery.toLocaleLowerCase('tr');
    return this.mentionables
      .filter(x => !q || x.insert.toLocaleLowerCase('tr').startsWith(q) || x.label.toLocaleLowerCase('tr').includes(q))
      .slice(0, 8);
  }

  onUserTextInput() {
    const ta = this.taRef?.nativeElement;
    const pos = ta?.selectionStart ?? this.userText.length;
    const upto = this.userText.substring(0, pos);
    const at = upto.lastIndexOf('@');
    if (at >= 0 && (at === 0 || /[\s(,;]/.test(upto[at - 1]))) {
      const q = upto.substring(at + 1);
      if (!/[\s@]/.test(q)) {
        this.mentionOpen = true;
        this.mentionQuery = q;
        this.mentionStart = at;
        return;
      }
    }
    this.mentionOpen = false;
  }

  onUserTextKeydown(ev: KeyboardEvent) {
    if (!this.mentionOpen) return;
    if (ev.key === 'Escape') { this.mentionOpen = false; return; }
    if ((ev.key === 'Enter' && !ev.ctrlKey) || ev.key === 'Tab') {
      const first = this.filteredMentions[0];
      if (first) {
        ev.preventDefault();
        this.pickMention(first);
      }
    }
  }

  pickMention(item: { insert: string }) {
    const end = this.mentionStart + 1 + this.mentionQuery.length;
    this.userText = this.userText.substring(0, this.mentionStart) + '@' + item.insert + ' ' + this.userText.substring(end);
    const caret = this.mentionStart + item.insert.length + 2;
    this.mentionOpen = false;
    setTimeout(() => {
      const ta = this.taRef?.nativeElement;
      if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
    });
  }

  // Mesaj içindeki @Ad ifadelerini vurgular (metin önce escape edilir).
  displayHtml(m: any): string {
    const esc = (m._display || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    return esc.replace(/@([A-Za-zÇĞİÖŞÜçğıöşü][\wÇĞİÖŞÜçğıöşü.-]*)/g, '<span class="mention">@$1</span>');
  }

  // ---- Katılımcı mesajı ----

  sendUserMessage() {
    const text = this.userText.trim();
    if (!text || this.sending || !this.me) return;
    this.sending = true;          // moderatör yanıtı gelene kadar input kilitli + busy ikon
    this.mentionOpen = false;

    // Kullanıcının kendi mesajını hemen göster (sunucu yanıtı gelince gerçeğiyle değişir).
    const temp: any = {
      id: -Date.now(), role: 'user', speakerCode: 'P' + this.me.id,
      speakerName: this.me.name, content: text, _display: text,
      createdAt: new Date().toISOString(), _temp: true
    };
    this.messages.push(temp);
    this.userText = '';
    this.scrollToBottom();

    this.api.PostData('meeting/user-message',
      { meetingId: this.meetingId, text, name: this.me.name, participantId: this.me.id },
      (resp: any) => {
        // Geçici mesajı kaldır; gerçek liste (kullanıcı + moderatör yönlendirmesi) merge edilir.
        this.messages = this.messages.filter(x => x !== temp);
        if (resp.meeting) this.applyMeeting(resp.meeting);
        if (resp.state) this.state = resp.state;
        if (resp.suggestFinalize) this.suggestFinalize = true;
        this.mergeMessages(resp.messages || [], true);
        this.sending = false;
        // Moderatör yönlendirmesinin ardından uzman cevabı için akışı sürdür.
        this.maybeContinue();
      }, () => {
        // Sunucu hata verdiyse geçici balonu kaldır: mesaj kaydolduysa poll
        // gerçek kaydı getirir, kaydolmadıysa mükerrer/hayalet balon kalmaz.
        this.messages = this.messages.filter(x => x !== temp);
        this.sending = false;
      });
  }

  finalize() {
    if (this.finalizing) return;
    this.finalizing = true;
    this.paused = true;
    this.api.PostData('meeting/finalize', { meetingId: this.meetingId }, (resp: any) => {
      this.applyMeeting(resp.meeting);
      this.finalizing = false;
      this.showReport = true;
      this.load();
    }, () => this.finalizing = false);
  }

  // ---- Rapor indirme ----

  downloadMarkdown() {
    if (!this.meeting?.finalReport) return;
    const blob = new Blob([this.meeting.finalReport], { type: 'text/markdown;charset=utf-8' });
    this.triggerDownload(blob, `toplanti-${this.meetingId}-rapor.md`);
  }

  async downloadPdf() {
    if (!this.reportRef) return;
    const html2pdf = (await import('html2pdf.js')).default;
    html2pdf().set({
      margin: 12,
      filename: `toplanti-${this.meetingId}-rapor.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(this.reportRef.nativeElement).save();
  }

  private triggerDownload(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---- Görünüm yardımcıları ----

  isMine(m: any): boolean {
    return m.role === 'user' && !!this.me && m.speakerCode === 'P' + this.me.id;
  }

  avatarClass(m: any): string {
    if (m.role === 'moderator') return 'avatar-moderator';
    if (m.role === 'user') return 'avatar-ceo';
    const i = this.experts.findIndex(e => e.code === m.speakerCode);
    return 'avatar-' + (i >= 0 ? i % 6 : 0);
  }

  initials(m: any): string {
    if (m.role === 'moderator') return 'M';
    // Uzman/katılımcı: kod (TEDARİK) değil, kişinin adının ilk harfi.
    const name = (m.speakerName || m.speakerCode || '?').trim();
    return (name.charAt(0) || '?').toLocaleUpperCase('tr');
  }

  speakerLabel(m: any): string {
    if (m.role === 'moderator') return 'Moderatör';
    if (m.role === 'user') return (m.speakerName || 'Katılımcı') + (this.isMine(m) ? ' (siz)' : '');
    return `${m.speakerCode} — ${m.speakerName || ''}`;
  }

  statusLabel(s: string): string {
    const map: any = { Setup: 'Hazırlık', Active: 'Devam Ediyor', WrapUp: 'Toparlama Modu', Closing: 'Kapanış — Rapor Bekleniyor', Completed: 'Tamamlandı' };
    return map[s] || s;
  }

  // ---- Uzman rol etiketleri (Toplantı Durumu) ----

  get approvedExperts(): any[] {
    return this.experts.filter(e => e.isApproved);
  }

  // Renk sınıfı (avatar/rol sırasıyla): roster chip'i ve inline etiket için ortak.
  roleClass(code: string): string {
    const i = this.approvedExperts.findIndex(e => e.code === code);
    return 'role-c' + ((i >= 0 ? i : 0) % this.rolePalette.length);
  }

  private escapeRe(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Durum metnindeki uzman adlarını "KOD · Ad" biçiminde renkli vurgular (ör. CTO · Serhan).
  // [innerHTML] ile basıldığı için inline stil yerine CSS sınıfı kullanılır (sanitizer güvenli).
  annotateRoles(text: string): string {
    let out = (text || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    for (const e of this.approvedExperts) {
      const name = (e.name || '').trim();
      if (!name || !e.code) continue;
      const cls = this.roleClass(e.code);
      // Ad, kelime sınırında; başında zaten kod (· ) yoksa tekrarı önle.
      const re = new RegExp('(^|[^\\wÇĞİÖŞÜçğıöşü·])(' + this.escapeRe(name) + ')(?![\\wÇĞİÖŞÜçğıöşü])', 'g');
      out = out.replace(re, (_m, pre, nm) =>
        `${pre}<span class="role-tag ${cls}">${e.code} · </span>${nm}`);
    }
    return out;
  }

  private scrollToBottom(delay = 50) {
    setTimeout(() => {
      const el = this.feedRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, delay);
  }
}
